import { useState, useCallback, useMemo, FormEvent } from 'react'
import styled, { keyframes } from 'styled-components'
import { useAuth } from '../../hooks/useAuth'
import { profileService } from '../../services/profileService'
import { tokens } from '../../styles/tokens'
import { ValidationViolation } from '../../types/auth'

// ─── Animations ───────────────────────────────────────────
const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`

// ─── Layout ───────────────────────────────────────────────
const Page = styled.div`
  max-width: 680px;
  animation: ${fadeUp} 0.3s ease;
`

const Eyebrow = styled.p`
  font-size: 10px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${tokens.purple400};
  margin-bottom: 8px;
`

const PageTitle = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  font-weight: 300;
  color: ${tokens.purple900};
  letter-spacing: -0.5px;
  margin-bottom: 32px;
`

const Card = styled.div`
  background: white;
  border-radius: 14px;
  border: 1px solid ${tokens.purple100};
  overflow: hidden;
  margin-bottom: 20px;
`

const CardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${tokens.purple100};
  display: flex;
  align-items: center;
  gap: 12px;
`

const CardIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: ${tokens.purple50};
  border: 1px solid ${tokens.purple100};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${tokens.purple400};
  flex-shrink: 0;
`

const CardTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 18px;
  font-weight: 400;
  color: ${tokens.purple900};
`

const CardBody = styled.div`
  padding: 24px;
`

// ─── Form elements ────────────────────────────────────────
const FieldGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`

const Label = styled.label`
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${tokens.purple200};
  font-weight: 500;
`

const Input = styled.input<{ $error?: boolean }>`
  padding: 10px 14px;
  background: white;
  border: 1px solid ${p => (p.$error ? '#f5c0d0' : tokens.purple100)};
  border-radius: 10px;
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
  color: ${tokens.purple900};
  outline: none;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: ${tokens.purple200};
  }
  &:focus {
    border-color: ${p => (p.$error ? '#f5c0d0' : tokens.purple400)};
    box-shadow: 0 0 0 3px
      ${p => (p.$error ? 'rgba(192, 67, 106, 0.1)' : 'rgba(155, 107, 181, 0.1)')};
  }
`

const Select = styled.select`
  padding: 10px 14px;
  background: white;
  border: 1px solid ${tokens.purple100};
  border-radius: 10px;
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
  color: ${tokens.purple900};
  outline: none;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  width: 100%;
  box-sizing: border-box;
  appearance: none;
  cursor: pointer;

  &:focus {
    border-color: ${tokens.purple400};
    box-shadow: 0 0 0 3px rgba(155, 107, 181, 0.1);
  }
`

const ErrorText = styled.p`
  font-size: 11px;
  color: #c0436a;
  margin-top: 2px;
`

const FullField = styled(Field)`
  margin-bottom: 16px;
`

// ─── Footer ───────────────────────────────────────────────
const CardFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${tokens.purple100};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${tokens.purple50};
`

const SaveButton = styled.button<{ $loading?: boolean }>`
  padding: 10px 20px;
  background: linear-gradient(135deg, ${tokens.purple600}, ${tokens.purple400});
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  cursor: ${p => (p.$loading ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.$loading ? 0.7 : 1)};
  transition:
    opacity 0.2s,
    transform 0.15s;
  box-shadow: 0 4px 12px rgba(107, 58, 138, 0.25);

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`

// ─── Feedback messages ────────────────────────────────────
const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #2e7d32;
  background: #f1f8f1;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 8px 12px;
`

const ErrorMessage = styled.div`
  font-size: 12px;
  color: #c0436a;
  background: #fdeef4;
  border: 1px solid #f5c0d0;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 16px;
`

// ─── Avatar ───────────────────────────────────────────────
const AvatarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${tokens.purple100};
`

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${tokens.purple600}, ${tokens.purple400});
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Cormorant Garamond', serif;
  font-size: 28px;
  color: white;
  flex-shrink: 0;
`

const AvatarInfo = styled.div``

const AvatarName = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: ${tokens.purple900};
  margin-bottom: 2px;
`

const AvatarEmail = styled.p`
  font-size: 12px;
  color: ${tokens.purple400};
`

// ─── Password strength ────────────────────────────────────
const StrengthBar = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 4px;
`

const StrengthSegment = styled.div<{ $active: boolean; $color: string }>`
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: ${p => (p.$active ? p.$color : tokens.purple100)};
  transition: background 0.3s;
`

const StrengthLabel = styled.p<{ $color: string }>`
  font-size: 11px;
  color: ${p => p.$color};
  margin-top: 4px;
`

const getPasswordStrength = (password: string): { score: number; label: string; color: string } => {
  if (!password) return { score: 0, label: '', color: 'transparent' }
  let score = 0
  if (password.length >= 8) score += 1
  if (password.length >= 12) score += 1
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1
  if (/\d/.test(password)) score += 1
  if (/[^A-Za-z0-9]/.test(password)) score += 1

  if (score <= 1) return { score, label: 'Weak', color: '#e24b4a' }
  if (score <= 3) return { score, label: 'Fair', color: '#ba7517' }
  return { score, label: 'Strong', color: '#3b6d11' }
}

// ─── Icons ────────────────────────────────────────────────
function PersonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function LockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="3" y="7" width="10" height="8" rx="2" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M5 7V5a3 3 0 0 1 6 0v2"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <circle cx="8" cy="11" r="1" fill="currentColor" />
    </svg>
  )
}

// ─── Component ────────────────────────────────────────────
export function ProfilePage() {
  const { user, token } = useAuth()
  const svc = useMemo(() => profileService(token), [token])

  // ── Personal info state ───────────────────────────────
  const [name, setName] = useState(user?.name ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [locale, setLocale] = useState(user?.locale ?? 'en')

  const [infoSaving, setInfoSaving] = useState(false)
  const [infoSuccess, setInfoSuccess] = useState(false)
  const [infoErrors, setInfoErrors] = useState<Record<string, string>>({})

  // ── Password state ────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [pwSaving, setPwSaving] = useState(false)
  const [pwSuccess, setPwSuccess] = useState(false)
  const [pwErrors, setPwErrors] = useState<Record<string, string>>({})

  const strength = getPasswordStrength(newPassword)

  // ── Helpers ───────────────────────────────────────────
  const violationsToMap = (violations: ValidationViolation[]): Record<string, string> => {
    return violations.reduce<Record<string, string>>((acc, v) => {
      acc[v.propertyPath] = v.message
      return acc
    }, {})
  }

  const initials = name
    ? name
        .split(' ')
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : (email[0]?.toUpperCase() ?? '?')

  // ── Submit personal info ──────────────────────────────
  const handleInfoSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      setInfoErrors({})
      setInfoSuccess(false)
      setInfoSaving(true)

      try {
        await svc.update({ name, email, locale })
        setInfoSuccess(true)
        setTimeout(() => setInfoSuccess(false), 3000)
      } catch (err: unknown) {
        const error = err as { violations?: ValidationViolation[] }
        if (error.violations) {
          setInfoErrors(violationsToMap(error.violations))
        }
      } finally {
        setInfoSaving(false)
      }
    },
    [svc, name, email, locale]
  )

  // ── Submit password ───────────────────────────────────
  const handlePasswordSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      setPwErrors({})
      setPwSuccess(false)

      if (newPassword !== confirmPassword) {
        setPwErrors({ confirmPassword: 'Passwords do not match' })
        return
      }

      setPwSaving(true)

      try {
        await svc.changePassword({ currentPassword, newPassword, confirmPassword })
        setPwSuccess(true)
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setTimeout(() => setPwSuccess(false), 3000)
      } catch (err: unknown) {
        const error = err as { violations?: ValidationViolation[] }
        if (error.violations) {
          setPwErrors(violationsToMap(error.violations))
        }
      } finally {
        setPwSaving(false)
      }
    },
    [svc, currentPassword, newPassword, confirmPassword]
  )

  return (
    <Page>
      <Eyebrow>Account</Eyebrow>
      <PageTitle>Profile</PageTitle>

      {/* ── Personal info card ──────────────────── */}
      <Card>
        <CardHeader>
          <CardIcon>
            <PersonIcon />
          </CardIcon>
          <CardTitle>Personal information</CardTitle>
        </CardHeader>

        <form onSubmit={handleInfoSubmit}>
          <CardBody>
            <AvatarSection>
              <Avatar>{initials}</Avatar>
              <AvatarInfo>
                <AvatarName>{name || 'No name set'}</AvatarName>
                <AvatarEmail>{email}</AvatarEmail>
              </AvatarInfo>
            </AvatarSection>

            <FieldGroup>
              <Field>
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  $error={!!infoErrors.name}
                  onChange={e => setName(e.target.value)}
                />
                {infoErrors.name && <ErrorText>{infoErrors.name}</ErrorText>}
              </Field>

              <Field>
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@carameru.com"
                  value={email}
                  $error={!!infoErrors.email}
                  onChange={e => setEmail(e.target.value)}
                />
                {infoErrors.email && <ErrorText>{infoErrors.email}</ErrorText>}
              </Field>
            </FieldGroup>

            <FullField>
              <Label htmlFor="locale">Preferred language</Label>
              <Select id="locale" value={locale} onChange={e => setLocale(e.target.value)}>
                <option value="en">English</option>
                <option value="es">Español</option>
              </Select>
            </FullField>
          </CardBody>

          <CardFooter>
            {infoSuccess ? <SuccessMessage>Profile updated successfully</SuccessMessage> : <div />}
            <SaveButton type="submit" $loading={infoSaving} disabled={infoSaving}>
              {infoSaving ? 'Saving…' : 'Save changes'}
            </SaveButton>
          </CardFooter>
        </form>
      </Card>

      {/* ── Password card ───────────────────────── */}
      <Card>
        <CardHeader>
          <CardIcon>
            <LockIcon />
          </CardIcon>
          <CardTitle>Change password</CardTitle>
        </CardHeader>

        <form onSubmit={handlePasswordSubmit}>
          <CardBody>
            {pwErrors.general && <ErrorMessage>{pwErrors.general}</ErrorMessage>}

            <FullField>
              <Label htmlFor="currentPassword">Current password</Label>
              <Input
                id="currentPassword"
                type="password"
                placeholder="Enter current password"
                value={currentPassword}
                $error={!!pwErrors.currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
              />
              {pwErrors.currentPassword && <ErrorText>{pwErrors.currentPassword}</ErrorText>}
            </FullField>

            <FieldGroup>
              <Field>
                <Label htmlFor="newPassword">New password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Min. 8 characters"
                  value={newPassword}
                  $error={!!pwErrors.newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                />
                {newPassword && (
                  <>
                    <StrengthBar>
                      {[1, 2, 3, 4, 5].map(i => (
                        <StrengthSegment
                          key={i}
                          $active={strength.score >= i}
                          $color={strength.color}
                        />
                      ))}
                    </StrengthBar>
                    <StrengthLabel $color={strength.color}>{strength.label}</StrengthLabel>
                  </>
                )}
                {pwErrors.newPassword && <ErrorText>{pwErrors.newPassword}</ErrorText>}
              </Field>

              <Field>
                <Label htmlFor="confirmPassword">Confirm new password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Repeat new password"
                  value={confirmPassword}
                  $error={!!pwErrors.confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
                {pwErrors.confirmPassword && <ErrorText>{pwErrors.confirmPassword}</ErrorText>}
              </Field>
            </FieldGroup>
          </CardBody>

          <CardFooter>
            {pwSuccess ? <SuccessMessage>Password changed successfully</SuccessMessage> : <div />}
            <SaveButton
              type="submit"
              $loading={pwSaving}
              disabled={pwSaving || !currentPassword || !newPassword || !confirmPassword}
            >
              {pwSaving ? 'Updating…' : 'Update password'}
            </SaveButton>
          </CardFooter>
        </form>
      </Card>
    </Page>
  )
}
